/**
 * Stakeholder Detail Route
 *
 * Displays comprehensive stakeholder metadata
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Outlet, useLocation } from '@tanstack/react-router';
import { useStakeholder } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import {
  DetailSkeleton,
  EmptyState,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { deleteStakeholder, ApiError } from '../../lib/apiClient';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import './stakeholderId.css';

export const Route = createFileRoute('/stakeholders/$stakeholderId')({
  component: StakeholderDetailPage,
});

const STAKEHOLDER_TYPE_LABELS: Record<string, string> = {
  PartnerAgencyOwner: 'Partner Agency Owner',
  PartnerOwner: 'Partner Owner',
};

function StakeholderDetailPage(): JSX.Element {
  const { stakeholderId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If on a child route (edit), render only the outlet
  const isChildRoute = location.pathname.endsWith('/edit');

  // Fetch stakeholder data
  const { data, loading, error: fetchError } = useStakeholder(stakeholderId);
  const stakeholder = data?.stakeholder;

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (isChildRoute) {
    return <Outlet />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="stakeholder-detail-page">
        <DetailSkeleton cards={4} />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="stakeholder-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load stakeholder"
          description={fetchError.message}
          actionLabel="Back to Stakeholders"
          onAction={() => navigate({ to: '/stakeholders', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  // Not found state
  if (!stakeholder) {
    return (
      <div className="stakeholder-detail-page">
        <EmptyState
          icon="search_off"
          title="Stakeholder not found"
          description={`No stakeholder found with ID: ${stakeholderId}`}
          actionLabel="Back to Stakeholders"
          onAction={() => navigate({ to: '/stakeholders', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  // Format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle edit action
  const handleEdit = (): void => {
    navigate({ to: `/stakeholders/${stakeholder.entityId}/edit` as string });
  };

  // Handle delete action
  const handleDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      // Soft delete by default
      await deleteStakeholder(stakeholderId, false);

      // Refetch GraphQL data to update cache
      await apolloClient.refetchQueries({
        include: ['ListStakeholders'],
      });

      // Navigate back to stakeholders list
      navigate({ to: '/stakeholders', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
    }
  };

  return (
    <div className="stakeholder-detail-page">
      {/* Header */}
      <div className="stakeholder-detail-header">
        <div>
          <ForgeButton
            type="button"
            variant="text"
            onClick={() => navigate({ to: '/stakeholders', search: { page: 1, pageSize: 25 } })}
          >
            <IconElement name="arrow_back" aria-hidden="true" />
            Back to Stakeholders
          </ForgeButton>
          <h1 className="forge-typography--headline4">{stakeholder.name}</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {stakeholder.description || 'No description provided'}
          </p>
        </div>
        <div className="stakeholder-detail-actions">
          <PermissionGate permission="stakeholder:update">
            <ForgeButton type="button" variant="raised" onClick={handleEdit}>
              <IconElement name="edit" aria-hidden="true" />
              Edit
            </ForgeButton>
          </PermissionGate>
          <PermissionGate permission="stakeholder:delete">
            <ForgeButton type="button" variant="outlined" onClick={handleDelete}>
              <IconElement name="delete" aria-hidden="true" />
              Delete
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      <div className="stakeholder-detail-content">
        {/* Overview Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Overview</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Stakeholder ID</span>
              <span className="detail-value">{stakeholder.entityId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Type</span>
              <span className="detail-value">
                {STAKEHOLDER_TYPE_LABELS[stakeholder.stakeholderType] ??
                  stakeholder.stakeholderType}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Organization</span>
              <span className="detail-value">{stakeholder.organization || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Contact Information Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Contact Information</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Email</span>
              <span className="detail-value">
                {stakeholder.contactEmail ? (
                  <a href={`mailto:${stakeholder.contactEmail}`}>{stakeholder.contactEmail}</a>
                ) : (
                  '—'
                )}
              </span>
            </div>
          </div>
        </CardElement>

        {/* Metadata Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Metadata</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">
                {formatDate(stakeholder._createdAt)} by {stakeholder._createdBy}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">
                {formatDate(stakeholder._updatedAt)} by {stakeholder._updatedBy}
              </span>
            </div>
          </div>
        </CardElement>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteDialogOpen(false)}
          title="Delete Stakeholder"
          message="Are you sure you want to delete this stakeholder?"
          warning="This action will soft delete the stakeholder. It can be recovered by an administrator."
          itemName={stakeholder.name}
          isDeleting={isDeleting}
        />
      )}

      {/* Error Banner */}
      {error && <div className="detail-error-toast">{error}</div>}
    </div>
  );
}
